import { ChangeEvent, useCallback, useState } from 'react'
import TextInput from '../../shared_components/TextInput'
import { IPoolProps } from '../../types/reducerTypes'
import Pagination from '../../shared_components/Pagination'
import { Title } from '../Home/parts'
import { Box } from '../../shared_components/UI/Box'
import { HeaderTitle } from './styles/Headers'
import { Card } from '../../shared_components/Pools/PoolCard'
import LoadingState from '../../shared_components/Pools/PoolCard/LoadingState'
import { formatPool } from '../Home/useUpcomingProjects'
import styled from 'styled-components'
import { Background, ForceFullWidth } from '../../shared_components/Hero/styled'
import { LaunchLogo } from '../Home/Launch'
import {
  SelectLabel,
  SelectMenu,
  SelectOption,
} from '../../shared_components/UI/Select'

const NoResults = styled.p`
  color: ${({ theme }) => theme.colors.brandPrimary};
  font-size: 32px;
  font-weight: 300;
  line-height: 1.2;
  text-align: center;
  text-transform: uppercase;
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: 100%;
  width: 620px;
  word-break: break-word;
`

const SearchFiltersBox = styled(Box)`
  gap: 32px;
  align-items: flex-end;

  @media screen and (max-width: 1000px) {
    flex-direction: column;
    gap: 16px;
  }
`

const statusOptions = {
  all: { label: 'All', value: 'all' },
  upcoming: { label: 'Upcoming', value: 'upcoming' },
  live: { label: 'Live', value: 'live' },
  completed: { label: 'Completed', value: 'completed' },
} as const

const sortOptions = {
  status: { label: 'Status', value: 'status' },
  mostTraction: { label: 'Most Traction', value: 'mostTraction' },
  recentlyLaunched: { label: 'Recently Launched', value: 'recentlyLaunched' },
  closingSoon: { label: 'Closing Soon', value: 'closingSoon' },
  mostFunded: { label: 'Most Funded', value: 'mostFunded' },
} as const

type Props = {
  isLoading: boolean
  pools: IPoolProps[]
  total: number
  limit: number
  offset: number
  search: string
  onSearch: (search: string) => void
  status: string
  onStatusChange: (status: string) => void
  sort: string
  onSortChange: (sort: string) => void
  onNext: () => void
  onPrev: () => void
  goToPage: (page: number) => void
}

const AllPools = ({
  isLoading,
  pools,
  total,
  limit,
  offset,
  search,
  onSearch,
  status,
  onStatusChange,
  sort,
  onSortChange,
  onNext,
  onPrev,
  goToPage,
}: Props) => {
  const [displayView, setDisplayView] = useState<'card' | 'table'>('card')

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onSearch(event.target.value)
    },
    [onSearch],
  )

  const cardPools = formatPool(pools)

  return (
    <>
      <ForceFullWidth position="static">
        <Background
          backgroundUrl="/all-projects-launch.jpeg"
          height="546px"
          mobileH="286px"
        />
      </ForceFullWidth>
      <Box dc g={40} marginBottom={128} relative z={1} marginTop={68}>
        <LaunchLogo />
        <Title>All Projects</Title>
        <SearchFiltersBox>
          <Box dc g={62}>
            <TextInput
              value={search}
              onChange={handleSearch}
              placeholder="Search by project name"
              rightContent={`${total} results`}
            />
          </Box>
          <Box fu aw g={32}>
            <Box dc fu aw g={8} relative>
              <SelectLabel htmlFor="status-select">Status</SelectLabel>
              <SelectMenu
                id="status-select"
                value={status}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const {
                    target: { value },
                  } = e
                  onStatusChange(value as typeof status)
                }}
              >
                {Object.values(statusOptions).map(({ label, value }, i) => {
                  return (
                    <SelectOption key={`select-status-${i}`} value={value}>
                      {label}
                    </SelectOption>
                  )
                })}
              </SelectMenu>
            </Box>
            <Box dc fu aw g={8} relative>
              <SelectLabel htmlFor="sort-select">Sort by</SelectLabel>
              <SelectMenu
                id="sort-select"
                value={sort}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                  const {
                    target: { value },
                  } = e

                  // the sorts we have here only work for pools in live status
                  if (value !== 'status') onStatusChange('live')

                  onSortChange(value as typeof sort)
                }}
              >
                {Object.values(sortOptions).map(({ label, value }, i) => {
                  return (
                    <SelectOption key={`select-sort-${i}`} value={value}>
                      {label}
                    </SelectOption>
                  )
                })}
              </SelectMenu>
            </Box>
          </Box>
        </SearchFiltersBox>
        <Box dc g={36}>
          {displayView === 'table' && (
            <>
              <HeaderTitle>
                <p>Project name</p>
                <p>Ratio</p>
                <p className="col-span-2">Progress</p>
                <p className="text-center">Network</p>
              </HeaderTitle>
              {pools.length > 0 &&
                pools.map((item: any) => {
                  return <></>
                  // (
                  // <Pool
                  //   key={item.address}
                  //   params={item.params}
                  //   address={item.address}
                  //   period={item.period}
                  //   urlLogo={item?.urlLogo}
                  //   chains={chains}
                  //   cmsDetails={
                  //     item?.params?.cmsDetails ??
                  //     item?.cmsDetails ??
                  //     item?.cmsData ??
                  //     item?.params?.cmsDetails
                  //   }
                  // />
                  // )
                })}
            </>
          )}
          {displayView === 'card' && (
            <Box g={32} fw jc centerMobile>
              {isLoading
                ? Array.from({ length: limit }).map((_, i) => (
                    <LoadingState key={i} />
                  ))
                : cardPools.map((item: any) => {
                    return (
                      <Card
                        key={item.id}
                        {...item}
                        cmsData={{ ...item?.cmsData }}
                      />
                    )
                  })}
            </Box>
          )}
          {pools.length === 0 && !search && (
            <Box jc>
              <NoResults>There are currently no projects</NoResults>
            </Box>
          )}
          {pools.length === 0 && search && (
            <Box jc>
              <NoResults>
                No projects found for search “{search.trim()}”
              </NoResults>
            </Box>
          )}
          <Pagination
            total={total}
            limit={limit}
            offset={offset}
            onNext={onNext}
            onPrev={onPrev}
            goToPage={goToPage}
          />
        </Box>
      </Box>
    </>
  )
}

export default AllPools
export { chains } from './constants'
